import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    MapPinIcon,
    PlusIcon,
    FunnelIcon
} from '@heroicons/react/24/solid';

// Mapeo de rutas a nombres legibles en español
const routeLabels = {
    'admin': 'INICIO',
    'dashboard': 'DASHBOARD',
    'usuarios': 'USUARIOS',
    'doctores': 'DOCTORES',
    'visitas': 'VISITAS',
    'empleados': 'EMPLEADOS',
    'sucursales': 'SUCURSALES',
    'especialidades': 'ESPECIALIDADES',
    'instituciones': 'INSTITUCIONES',
    'paqueteria': 'PAQUETERÍA',
    'proveedores': 'PROVEEDORES',
    'tipo-empleados': 'TIPO EMPLEADOS',
    'paises': 'PAÍSES',
    'estados': 'ESTADOS',
    'municipios': 'MUNICIPIOS',
    'colonias': 'COLONIAS',
    'agenda': 'AGENDA',
    'servicios': 'SERVICIOS',
    'categorias': 'CATEGORÍAS',
    'pedidos': 'PEDIDOS',
    'clientes': 'CLIENTES',
    'cupones': 'CUPONES',
    'roles': 'ROLES',
    'reportes': 'REPORTES',
    'ingresos': 'INGRESOS',
    'catalogos': 'CATÁLOGOS',
    'configuraciones': 'CONFIGURACIONES',
    'new': 'NUEVO',
    'edit': 'EDITAR',
    'detail': 'DETALLE',
    'perfil': 'PERFIL'
};

// Obtener el label legible de una ruta
const getRouteLabel = (segment) => {
    if (!segment) return '';
    // Si es un número (ID), no lo mostramos como breadcrumb principal
    if (/^\d+$/.test(segment)) return null;
    return routeLabels[segment.toLowerCase()] || segment.toUpperCase();
};

const Breadcrumb = ({ hasButton, hasModalButton, HasFilters, actual, onModalClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    // Procesar la ruta actual para generar breadcrumbs
    useEffect(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        let accumulatedPath = '';
        
        const crumbs = pathSegments.map((segment, index) => {
            accumulatedPath += `/${segment}`;
            const label = getRouteLabel(segment);

            // Si el segmento es 'detail' o 'perfil' y el siguiente segmento es un ID numérico,
            // incluimos ese ID en la ruta del breadcrumb (ej: /doctores/detail/2)
            let path = accumulatedPath;
            const nextSegment = pathSegments[index + 1];
            if ((segment === 'detail' || segment === 'perfil') && /^\d+$/.test(nextSegment)) {
                path += `/${nextSegment}`;
            }

            return {
                segment,
                label,
                path,
                isLast: index === pathSegments.length - 1,
                isId: /^\d+$/.test(segment)
            };
        }).filter(crumb => crumb.label !== null); // Filtrar IDs numéricos
        
        setBreadcrumbs(crumbs);
    }, [location]);

    // Determinar el módulo principal (primera sección después de admin)
    const mainModule = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        // El módulo principal es el segundo segmento (después de 'admin')
        if (pathSegments.length > 1) {
            return getRouteLabel(pathSegments[1]);
        }
        return 'INICIO';
    }, [location]);

    // Navegar a crear nuevo
    const handleNewClick = () => {
        navigate(location.pathname + '/new');
    };

    return (
        <div 
            className="w-full px-1 py-1 rounded-lg mb-1"
           // style={{ background: 'linear-gradient(135deg, #11cdef 0%, #0da5c0 85%, #0891b2 100%)' }}
        >
            <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Sección izquierda: Icono + Módulo principal + Breadcrumbs */}
                <div className="flex items-center gap-8">
                    {/* Icono grande + Nombre del módulo */}
                    <div className="flex items-center gap-3">
                        <MapPinIcon className="w-7 h-7 text-white" />
                        <span className="font-bold text-white text-xl tracking-wide">{mainModule}</span>
                    </div>

                    {/* Breadcrumbs con separador de punto */}
                    <nav className="flex items-center centrar" aria-label="Breadcrumb" style={{backgroundColor:"transparent",boxShadow:"none"}}>
                        <ol className="flex items-center flex-wrap">
                            {/* Breadcrumbs dinámicos (desde el segundo elemento) */}
                            {breadcrumbs.slice(1).map((crumb, index) => (
                                <li key={crumb.path} className="flex items-center">
                                    {/* Separador punto */}
                                    <span className="text-white text-3xl mx-5 leading-none font-bold">•</span>
                                    
                                    {crumb.isLast && !actual ? (
                                        <span className="text-base font-semibold text-white tracking-wide">
                                            {crumb.label}
                                        </span>
                                    ) : (
                                        <Link 
                                            to={crumb.path}
                                            className="text-base text-white/90 hover:text-white hover:underline transition-colors tracking-wide font-medium"
                                        >
                                            {crumb.label}
                                        </Link>
                                    )}
                                </li>
                            ))}

                            {/* Mostrar "actual" si se proporciona (ej: nombre del doctor) */}
                            {actual && (
                                <li className="flex items-center">
                                    <span className="text-white text-3xl mx-5 leading-none font-bold">•</span>
                                    <span className="text-base font-semibold text-white tracking-wide max-w-50 truncate" title={actual}>
                                        {actual.toUpperCase()}
                                    </span>
                                </li>
                            )}
                        </ol>
                    </nav>
                </div>

                {/* Sección derecha: Botones de acción con icono y texto */}
                <div className="flex items-center gap-3">
                    {hasModalButton && (
                        onModalClick ? (
                            <button
                                onClick={onModalClick}
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-base font-semibold text-[#11cdef] bg-white rounded-lg shadow-md cursor-pointer transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-[#11cdef] hover:text-white hover:shadow-2xl"
                            >
                                <PlusIcon className="w-6 h-6" />
                                <span>Nuevo</span>
                            </button>
                        ) : (
                            <a 
                                href={`#${hasModalButton}`}
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-base font-semibold text-[#11cdef] bg-white rounded-lg shadow-md cursor-pointer transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-[#11cdef] hover:text-white hover:shadow-2xl"
                            >
                                <PlusIcon className="w-6 h-6" />
                                <span>Nuevo</span>
                            </a>
                        )
                    )}
                    
                    {hasButton && (
                        <button 
                            onClick={handleNewClick}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-base font-semibold text-[#11cdef] bg-white rounded-lg shadow-md cursor-pointer transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-[#11cdef] hover:text-white hover:shadow-2xl hover:border-white border-2"
                        >
                            <PlusIcon className="w-6 h-6" />
                            <span>Nuevo</span>
                        </button>
                    )}
                    
                    {HasFilters && (
                        <button className="inline-flex items-center gap-2 px-5 py-2.5 text-base font-semibold text-[#11cdef] bg-white rounded-lg shadow-md cursor-pointer transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-[#11cdef] hover:text-white hover:shadow-2xl ">
                            <FunnelIcon className="w-6 h-6" />
                            <span>Filtros</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Breadcrumb;